export const getPositionChronologyTime = (position) => (
  position.close_time || position.time_close || position.time_open || position.open_time || position.time || 0
);

export const getNetTradeProfit = (position = {}) => {
  const explicitNetProfit = position.net_profit;
  if (explicitNetProfit !== null && explicitNetProfit !== undefined && explicitNetProfit !== "") {
    const numericNetProfit = Number(explicitNetProfit);
    if (Number.isFinite(numericNetProfit)) {
      return numericNetProfit;
    }
  }

  return (
    (Number(position.profit) || 0)
    + (Number(position.commission) || 0)
    + (Number(position.swap) || 0)
  );
};

const getCashFlowChronologyTime = (cashflow = {}) => (
  cashflow.occurred_at || cashflow.time || cashflow.timestamp || 0
);

const getEventTimestamp = (value) => {
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
};

export const buildPerformanceTimeline = (positions = [], cashflows = [], startingAmount = 0) => {
  const normalizedStartingAmount = Number(startingAmount);
  const initialBalance = Number.isFinite(normalizedStartingAmount) ? normalizedStartingAmount : 0;
  const events = [
    ...positions.map((position) => ({
      type: "trade",
      time: getPositionChronologyTime(position),
      amount: getNetTradeProfit(position),
      source: position,
    })),
    ...cashflows.map((cashflow) => ({
      type: "cashflow",
      time: getCashFlowChronologyTime(cashflow),
      amount: Number(cashflow.amount) || 0,
      source: cashflow,
    })),
  ].sort((a, b) => {
    const timeDifference = getEventTimestamp(a.time) - getEventTimestamp(b.time);
    if (timeDifference !== 0) {
      return timeDifference;
    }
    if (a.type === b.type) {
      return 0;
    }
    return a.type === "cashflow" ? -1 : 1;
  });

  let balance = initialBalance;
  let cashAdjustedPeak = initialBalance;
  let performanceIndex = 1;
  let peakPerformanceIndex = 1;
  let maxDrawdown = 0;
  let maxDrawdownPercent = 0;
  let currentDrawdown = 0;
  let currentDrawdownPercent = 0;
  const tradeReturns = [];
  const firstEventTime = events.length ? events[0].time : "";
  const points = [{
    time: firstEventTime,
    balance,
    drawdown: 0,
    drawdownPercent: 0,
    eventType: "start",
    amount: 0,
  }];

  events.forEach((event) => {
    if (event.type === "cashflow") {
      balance += event.amount;
      cashAdjustedPeak += event.amount;
      cashAdjustedPeak = Math.max(cashAdjustedPeak, balance);
    } else {
      const tradeReturn = balance !== 0 ? event.amount / balance : 0;
      tradeReturns.push(tradeReturn);
      performanceIndex *= 1 + tradeReturn;
      peakPerformanceIndex = Math.max(peakPerformanceIndex, performanceIndex);

      balance += event.amount;
      cashAdjustedPeak = Math.max(cashAdjustedPeak, balance);
      currentDrawdown = Math.max(0, cashAdjustedPeak - balance);
      currentDrawdownPercent = peakPerformanceIndex > 0
        ? Math.max(0, ((peakPerformanceIndex - performanceIndex) / peakPerformanceIndex) * 100)
        : 0;
      maxDrawdown = Math.max(maxDrawdown, currentDrawdown);
      maxDrawdownPercent = Math.max(maxDrawdownPercent, currentDrawdownPercent);
    }

    points.push({
      time: event.time,
      balance,
      drawdown: currentDrawdown,
      drawdownPercent: currentDrawdownPercent,
      eventType: event.type,
      amount: event.amount,
      source: event.source,
    });
  });

  return {
    initialBalance,
    endingBalance: balance,
    percentageGain: (performanceIndex - 1) * 100,
    maxDrawdown,
    maxDrawdownPercent,
    tradeReturns,
    points,
  };
};

export const getPositionStats = (positions = [], accountInfo = {}, startingAmount = 0, cashflows = []) => {
  if (!positions || positions.length <= 0) {
    const emptyTimeline = buildPerformanceTimeline([], cashflows, startingAmount);
    return {
      total_trades: 0,
      wins: 0,
      losses: 0,
      scratch: 0,
      win_ratio: 0,
      total_profit: 0,
      percentage_gain: 0,
      long_trade: 0,
      short_trade: 0,
      initial_balance: emptyTimeline.initialBalance,
      max_drawdown: parseFloat(emptyTimeline.maxDrawdown.toFixed(2)),
      max_drawdown_percent: parseFloat(emptyTimeline.maxDrawdownPercent.toFixed(2)),
      average_trade_duration: 0,
      average_winning_trade_duration: 0,
      average_losing_trade_duration: 0,
      sharpe_ratio: 0,
      average_pips: 0,
      average_risk_reward: 0,
      gross_profit: 0,
      gross_loss: 0,
      average_win: 0,
      average_loss: 0,
      profit_factor: 0,
      long_trade_percent: 0,
      short_trade_percent: 0,
      max_consecutive_wins: 0,
      max_consecutive_losses: 0,
      best_symbol: null,
      best_symbol_profit: 0,
      best_magic: null,
      best_magic_profit: 0,
    };
  }

  const normalizedStartingAmount = Number(startingAmount);
  const sortedPositions = [...positions].sort((a, b) => {
    const aTime = new Date(getPositionChronologyTime(a)).getTime();
    const bTime = new Date(getPositionChronologyTime(b)).getTime();
    return aTime - bTime;
  });

  let wins = 0;
  let losses = 0;
  let scratch = 0;
  let totalProfit = 0.0;
  let totalTrades = 0;
  let longTrades = 0;
  let shortTrades = 0;
  let totalDuration = 0;
  let winningTradeDuration = 0;
  let losingTradeDuration = 0;
  let totalPips = 0.0;
  let totalRiskReward = 0.0;
  let totalRiskRewardTrades = 0;
  let grossProfit = 0.0;
  let grossLoss = 0.0;
  let averageWin = 0.0;
  let averageLoss = 0.0;
  let maxConsecutiveWins = 0;
  let maxConsecutiveLosses = 0;
  let currentConsecutiveWins = 0;
  let currentConsecutiveLosses = 0;
  const safeCurrentBalance = Number(accountInfo.balance) || 0;
  const riskFreeRate = 0;
  const symbolPerformance = {};
  const magicPerformance = {};

  for (const deal of sortedPositions) {
    const dealType = deal.type;
    if (dealType !== 0 && dealType !== 1) {
      continue;
    }

    const tradeDuration = Number(deal.duration) || 0;
    totalDuration += tradeDuration;
    const profit = getNetTradeProfit(deal);
    const pips = Number(deal.pips) || 0;

    const symbol = deal.symbol;
    if (!symbolPerformance[symbol]) {
      symbolPerformance[symbol] = { totalProfit: 0, trades: 0 };
    }
    symbolPerformance[symbol].totalProfit += profit;
    symbolPerformance[symbol].trades += 1;

    const magic = deal.magic;
    if (!magicPerformance[magic]) {
      magicPerformance[magic] = { totalProfit: 0, trades: 0 };
    }
    magicPerformance[magic].totalProfit += profit;
    magicPerformance[magic].trades += 1;

    if (dealType === 0) {
      longTrades += 1;
    } else if (dealType === 1) {
      shortTrades += 1;
    }

    totalProfit += profit;
    totalPips += pips;
    totalTrades += 1;

    if (deal.risk_reward_ratio > 0) {
      totalRiskReward += deal.risk_reward_ratio;
      totalRiskRewardTrades += 1;
    }

    if (profit > 0) {
      wins += 1;
      grossProfit += profit;
      winningTradeDuration += tradeDuration;
      currentConsecutiveWins += 1;
      currentConsecutiveLosses = 0;
      maxConsecutiveWins = Math.max(maxConsecutiveWins, currentConsecutiveWins);
    } else if (profit < 0) {
      losses += 1;
      grossLoss += Math.abs(profit);
      losingTradeDuration += tradeDuration;
      currentConsecutiveLosses += 1;
      currentConsecutiveWins = 0;
      maxConsecutiveLosses = Math.max(maxConsecutiveLosses, currentConsecutiveLosses);
    } else {
      scratch += 1;
      currentConsecutiveWins = 0;
      currentConsecutiveLosses = 0;
    }
  }

  let bestSymbol = null;
  let bestSymbolProfit = -Infinity;
  for (const [symbol, data] of Object.entries(symbolPerformance)) {
    if (data.totalProfit > bestSymbolProfit) {
      bestSymbol = symbol;
      bestSymbolProfit = data.totalProfit;
    }
  }

  let bestMagic = null;
  let bestMagicProfit = -Infinity;
  for (const [magic, data] of Object.entries(magicPerformance)) {
    if (data.totalProfit > bestMagicProfit) {
      bestMagic = magic;
      bestMagicProfit = data.totalProfit;
    }
  }

  const hasPeriodCashflows = Array.isArray(cashflows) && cashflows.length > 0;
  const initialBalance = Number.isFinite(normalizedStartingAmount) && (normalizedStartingAmount > 0 || hasPeriodCashflows)
    ? normalizedStartingAmount
    : (safeCurrentBalance - totalProfit);
  const performanceTimeline = buildPerformanceTimeline(
    sortedPositions.filter((deal) => deal.type === 0 || deal.type === 1),
    cashflows,
    initialBalance,
  );

  const decisiveTrades = wins + losses;
  const winRatio = decisiveTrades > 0 ? (wins / decisiveTrades) * 100 : 0;

  const averageTradeDurationHours = totalTrades > 0 ? (totalDuration / totalTrades) : 0;
  const averageTradeDurationDays = averageTradeDurationHours / 24;
  const averageWinningTradeDurationHours = wins > 0 ? (winningTradeDuration / wins) : 0;
  const averageWinningTradeDurationDays = averageWinningTradeDurationHours / 24;
  const averageLosingTradeDurationHours = losses > 0 ? (losingTradeDuration / losses) : 0;
  const averageLosingTradeDurationDays = averageLosingTradeDurationHours / 24;
  const returns = performanceTimeline.tradeReturns;
  const averageReturn = returns.length > 0 ? returns.reduce((sum, r) => sum + r, 0) / returns.length : 0;
  const variance = returns.length > 0 ? returns.reduce((sum, r) => sum + Math.pow(r - averageReturn, 2), 0) / returns.length : 0;
  const standardDeviation = Math.sqrt(variance);
  const sharpeRatio = standardDeviation > 0 ? (averageReturn - riskFreeRate) / standardDeviation : 0;
  const avgPips = totalTrades > 0 ? (totalPips / totalTrades) : 0;
  const avgRisk = totalRiskRewardTrades > 0 ? (totalRiskReward / totalRiskRewardTrades) : 0;
  const longTradePercent = totalTrades > 0 ? (longTrades / totalTrades) * 100 : 0;
  const shortTradePercent = totalTrades > 0 ? (shortTrades / totalTrades) * 100 : 0;
  const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : (grossProfit > 0 ? Infinity : 0);
  averageWin = wins > 0 ? grossProfit / wins : 0;
  averageLoss = losses > 0 ? grossLoss / losses : 0;

  return {
    total_trades: totalTrades,
    wins,
    losses,
    scratch,
    win_ratio: parseFloat(winRatio.toFixed(2)),
    total_profit: parseFloat(totalProfit.toFixed(2)),
    percentage_gain: parseFloat(performanceTimeline.percentageGain.toFixed(2)),
    long_trade: longTrades,
    short_trade: shortTrades,
    initial_balance: initialBalance,
    max_drawdown: parseFloat(performanceTimeline.maxDrawdown.toFixed(2)),
    max_drawdown_percent: parseFloat(performanceTimeline.maxDrawdownPercent.toFixed(2)),
    average_trade_duration: parseFloat(averageTradeDurationDays.toFixed(2)),
    average_winning_trade_duration: parseFloat(averageWinningTradeDurationDays.toFixed(2)),
    average_losing_trade_duration: parseFloat(averageLosingTradeDurationDays.toFixed(2)),
    sharpe_ratio: parseFloat(sharpeRatio.toFixed(2)),
    average_pips: parseFloat(avgPips.toFixed(2)),
    average_risk_reward: parseFloat(avgRisk.toFixed(2)),
    gross_profit: parseFloat(grossProfit.toFixed(2)),
    gross_loss: parseFloat(grossLoss.toFixed(2)),
    average_win: parseFloat(averageWin.toFixed(2)),
    average_loss: parseFloat(averageLoss.toFixed(2)),
    profit_factor: Number.isFinite(profitFactor) ? parseFloat(profitFactor.toFixed(2)) : "Inf",
    long_trade_percent: parseFloat(longTradePercent.toFixed(2)),
    short_trade_percent: parseFloat(shortTradePercent.toFixed(2)),
    max_consecutive_wins: maxConsecutiveWins,
    max_consecutive_losses: maxConsecutiveLosses,
    best_symbol: bestSymbol,
    best_symbol_profit: parseFloat(bestSymbolProfit.toFixed(2)),
    best_magic: bestMagic,
    best_magic_profit: parseFloat(bestMagicProfit.toFixed(2)),
  };
};
